import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: theme.shadows[2],
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#1e2836",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  textTransform: "uppercase",
  fontSize: "0.875rem",
}));

const StyledChip = styled(Chip)(({ theme, chipcolor }) => ({
  backgroundColor: chipcolor || theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 500,
  borderRadius: "4px",
}));

const FilterBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const BillResultsTable = ({ bills, onViewDetails }) => {
  const [filter, setFilter] = React.useState("Tutti");

  const getChipColor = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case "luce":
        return "#FFA726"; // Orange
      case "gas":
        return "#42A5F5"; // Blue
      case "luce-gas":
        return "#66BB6A"; // Green
      default:
        return "#78909C"; // Gray
    }
  };

  const formatPeriod = (dataInizio, dataFine) => {
    if (!dataInizio || !dataFine) return "-";
    return `${dataInizio} - ${dataFine}`;
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return `${parseFloat(value).toFixed(2)} €`;
  };

  const filteredBills =
    filter === "Tutti"
      ? bills
      : bills.filter(
          (bill) => bill.tipoServizio?.toUpperCase() === filter.toUpperCase()
        );

  if (!bills || bills.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Confronto Bollette ({bills.length})
        </Typography>
        <FilterBox>
          <Typography variant="body2" fontWeight={500}>
            Filtra:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="Tutti">Tutti</MenuItem>
              <MenuItem value="LUCE">LUCE</MenuItem>
              <MenuItem value="GAS">GAS</MenuItem>
              <MenuItem value="LUCE-GAS">LUCE-GAS</MenuItem>
            </Select>
          </FormControl>
        </FilterBox>
      </Box>

      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell>FORNITORE</StyledTableCell>
              <StyledTableCell>TIPO</StyledTableCell>
              <StyledTableCell>INTESTATARIO</StyledTableCell>
              <StyledTableCell>PERIODO</StyledTableCell>
              <StyledTableCell align="right">
                TOTALE BOLLETTA
              </StyledTableCell>
              <StyledTableCell align="right">STIMA ANNUA</StyledTableCell>
              <StyledTableCell align="center">AZIONI</StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {filteredBills.map((bill, index) => (
              <TableRow
                key={bill.id || index}
                sx={{
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "light"
                        ? "#f5f5f5"
                        : "rgba(255,255,255,0.05)",
                  },
                }}
              >
                <TableCell>
                  <Typography fontWeight={500}>
                    {bill.fornitore || "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bill.tipologia === "privato" ? "LIBERO" : "BUSINESS"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StyledChip
                    label={bill.tipoServizio?.toUpperCase() || "LUCE"}
                    chipcolor={getChipColor(bill.tipoServizio)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {bill.nomeIntestatario || bill.nome}{" "}
                    {bill.cognomeIntestatario || bill.cognome}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatPeriod(bill.dataInizio, bill.dataFine)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bill.giorni ? `${bill.giorni} giorni` : ""}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>
                    {formatCurrency(bill.totaleBolletta || bill.totalebolletta)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600} color="primary">
                    {formatCurrency(bill.stimaAnnua)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => onViewDetails(bill)}
                    size="small"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  );
};

export default BillResultsTable;
